/**
 * usePhotoImpressions — observa elementos con [data-photo-track] dentro del
 * documento y dispara photo_impression cuando una foto estuvo ≥50% visible
 * por ≥800ms (una vez por foto por pageload).
 *
 * Uso: marcar cada foto con
 *   data-photo-track data-photo-src={url} data-photo-position={i}
 * y llamar usePhotoImpressions({ section, templateType, countryCode, ready })
 * una vez en la página/galería. `ready` permite re-escanear cuando la galería
 * termina de cargar (cambio de deps re-observa nodos nuevos).
 */

import { useEffect, useRef } from "react";
import { trackPhotoImpression, type PhotoTrackContext } from "@/lib/photoTracking";

const DWELL_MS = 800;
const VISIBILITY_THRESHOLD = 0.5;

interface Options extends PhotoTrackContext {
  /** Cambiá este valor cuando la lista de fotos cambie para re-observar. */
  ready?: unknown;
}

export function usePhotoImpressions({ section, templateType, countryCode, ready }: Options): void {
  const seenRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<Map<Element, number>>(new Map());

  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") return;

    const ctx: PhotoTrackContext = { section, templateType, countryCode };
    const seen = seenRef.current;
    const timers = timersRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          const src = el.dataset.photoSrc || "";
          if (!src || seen.has(src)) {
            observer.unobserve(el);
            continue;
          }
          if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
            if (!timers.has(el)) {
              const id = window.setTimeout(() => {
                timers.delete(el);
                if (seen.has(src)) return;
                seen.add(src);
                const pos = el.dataset.photoPosition;
                trackPhotoImpression(src, pos != null ? Number(pos) : undefined, ctx);
                observer.unobserve(el);
              }, DWELL_MS);
              timers.set(el, id);
            }
          } else {
            const id = timers.get(el);
            if (id != null) {
              window.clearTimeout(id);
              timers.delete(el);
            }
          }
        }
      },
      { threshold: [VISIBILITY_THRESHOLD] },
    );

    const observeAll = () => {
      document.querySelectorAll("[data-photo-track]").forEach((n) => {
        const el = n as HTMLElement;
        if (el.dataset.photoObserved === "1") return;
        el.dataset.photoObserved = "1";
        observer.observe(el);
      });
    };
    observeAll();

    // Las galerías se montan diferidas (skeleton → grid): observar también los
    // nodos que aparecen después sin depender de que el caller sepa cuándo.
    const mutations = new MutationObserver(() => observeAll());
    mutations.observe(document.body, { childList: true, subtree: true });

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
      mutations.disconnect();
      observer.disconnect();
      document
        .querySelectorAll('[data-photo-observed="1"]')
        .forEach((n) => delete (n as HTMLElement).dataset.photoObserved);
    };
  }, [section, templateType, countryCode, ready]);
}
